/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { TaskService } from 'app/core/services/task-service';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as Core from 'app/core/models/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';


@autoinject
export class TaskAppointments extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected taskId!: string;
    protected tasksConfig!: ITasksConfig;

    protected appointments: Array<Core.TaskAppointment> = [];
    protected saveAppointment: Core.TaskAppointment | null = null;

    protected includeInactive: boolean = false;
    protected includePast: boolean = false;
    protected readonly: boolean = false;

    protected validate = validate;
    protected isValid: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    public $modal!: EssModalCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly service: TaskService, ...rest: any[]) {
        super(...rest);
    }

    protected async activate(params: { taskId: string, tasksConfig: ITasksConfig, readonly: boolean }): Promise<void> {
        if(!(params.taskId && params.tasksConfig && params.tasksConfig.appointmentsConfig)) {
            throw new Error("Task Appointments was activated with missing parameters");
        }
        this.taskId = params.taskId;
        this.tasksConfig = params.tasksConfig;
        this.readonly = params.readonly;

        if(!this.service.isConfigured || this.service.config !== this.tasksConfig) {
            this.service.configure(this.tasksConfig);
        }

        this.loadAppointments();
    }


    ////****************** Events ***************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.loadAppointments();

        if(!!this.$modal) { this.$modal.close(); }
    }

    ////****************** Functions ************************************************////
    protected newAppointment() {
        this.saveAppointment = {
            id: null,
            dateTimeRange: {
                start: null,
                end: null
            }
        };

        this.$modal.open();
    }

    protected editAppointment(appointment: Core.TaskAppointment) {
        this.saveAppointment = deepClone(appointment);

        this.$modal.open();
    }

    /**
     * Handles the grid's sort event
     */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.loadAppointments();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadAppointments(): Promise<void> {
        if(!this.taskId) { return; }

        const options: StandardLoadOptions = {
            includeInactive: this.includeInactive,
            all: true,
            sorting: this.sorting
        }

        try {
            this.state.beginLoad();
            const response = await this.service.loadAppointmentsByTask(this.taskId!, options);
            this.appointments = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateAppointment(appointment: Core.TaskAppointment, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('core:task.taskAppointment.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            const response = await this.service.activateTaskAppointment(appointment.id!, isActive);
            if(response.isSuccessful) {
                this.loadAppointments();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
